import * as React from 'react';

import { Button, Col, ConsoleDialog, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { BrokeredLoadWizardStepInfo } from './BrokeredLoadWizardStepInfo';
import { BrokeredLoadWizardStepMaterials } from './BrokeredLoadWizardStepMaterials';

import {
  $brokeredLoadCreateForm,
  $createBrokeredLoad,
  $nextLoadDocumentNumber,
  $resetForm,
} from '~/modules/brokerage/_state/brokeredLoadCreateForm.state';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { brokerageRoutePaths } from '~/modules/brokerage/routes';
import { transformBrokeredLoadCreateInput } from '~/modules/brokerage/utils/brokeredLoadCreate';
import { StepIndicator } from '~/modules/trade/_components/_shared/StepIndicator';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

interface BrokeredLoadCreateProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface Step {
  number: number;
  title: string;
}

const STEP_COMPONENTS: Record<
  number,
  React.ComponentType<{ disabled?: boolean }>
> = {
  1: BrokeredLoadWizardStepInfo,
  2: BrokeredLoadWizardStepMaterials,
};

export const BrokeredLoadCreate = ({
  isOpen,
  onClose,
  onSuccess,
}: BrokeredLoadCreateProps) => {
  const t = useTranslations();
  const toast = useToast();
  const [, navigate] = useModuleLocation();

  const [currentStep, setCurrentStep] = React.useState(1);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const formData = useAtomValue($brokeredLoadCreateForm);
  const updateForm = useSetAtom($brokeredLoadCreateForm);
  const resetForm = useSetAtom($resetForm);
  const createLoadMutation = useSetAtom($createBrokeredLoad);
  const nextDocumentNumber = useSetAtom($nextLoadDocumentNumber);

  const wizardSteps: Step[] = React.useMemo(
    () => [
      { number: 1, title: t.brokerage.loads.create.wizard.steps.shipmentInfo },
      { number: 2, title: t.brokerage.loads.create.wizard.steps.materials },
    ],
    [t],
  );

  React.useEffect(() => {
    if (!isOpen) return;
    resetForm();
    setCurrentStep(1);
  }, [isOpen, resetForm]);

  const needsDocumentNumber = isOpen && !formData.documentNumber;

  React.useEffect(() => {
    if (!needsDocumentNumber) return;

    const abortController = new AbortController();

    nextDocumentNumber({})
      .then((result) => {
        if (abortController.signal.aborted) return;
        updateForm((prev) => ({
          ...prev,
          documentNumber: result.tradeLoadNextDocumentNumber,
        }));
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        toast.error(
          error instanceof Error
            ? error.message
            : t.brokerage.loads.messages.documentNumberError,
        );
        onClose();
      });

    return () => {
      abortController.abort();
    };
  }, [needsDocumentNumber, nextDocumentNumber, updateForm, onClose]);

  const handleClose = React.useCallback(() => {
    resetForm();
    setCurrentStep(1);
    onClose();
  }, [onClose, resetForm]);

  const handleBack = React.useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleNext = React.useCallback(() => {
    if (currentStep === 1) {
      const errors: string[] = [];
      if (!formData.supplierId) {
        errors.push(t.brokerage.loads.create.validation.supplierRequired);
      }
      if (!formData.facilityId) {
        errors.push(t.brokerage.loads.create.validation.facilityRequired);
      }
      if (!formData.expectedShipDate) {
        errors.push(
          t.brokerage.loads.create.validation.expectedShipDateRequired,
        );
      }
      if (errors.length > 0) {
        errors.forEach((error) => toast.error(error));
        return;
      }
    }

    if (currentStep < wizardSteps.length) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, wizardSteps.length, formData, toast, t]);

  const handleSuccess = React.useCallback(
    (loadId: string) => {
      onSuccess?.();
      resetForm();
      navigate(brokerageRoutePaths.loadDetail, {
        params: { id: loadId },
        query: { tab: 'materials' },
      });
    },
    [navigate, onSuccess, resetForm],
  );

  const handleSubmit = React.useCallback(async () => {
    try {
      setIsSubmitting(true);
      const result = await createLoadMutation(
        transformBrokeredLoadCreateInput(formData),
      );

      toast.success(t.brokerage.loads.messages.createSuccess);
      handleSuccess(result.tradeLoadCreate.id);
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.brokerage.loads.messages.createError,
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, createLoadMutation, toast, t, handleSuccess, handleClose]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === wizardSteps.length;

  const StepComponent = STEP_COMPONENTS[currentStep];

  return (
    <ConsoleDialog
      className="@BrokeredLoadCreate"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      size="fill"
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>{t.brokerage.loads.create.title}</span>
          </Row>
          <StepIndicator steps={wizardSteps} currentStep={currentStep} />
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      content={StepComponent ? <StepComponent disabled={isSubmitting} /> : null}
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div>
            {!isFirstStep && (
              <Button
                color="neutral"
                variant="outline"
                onPress={handleBack}
                isDisabled={isSubmitting}
              >
                {t.brokerage.loads.create.wizard.buttons.back}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              color="neutral"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              {t.brokerage.loads.create.cancel}
            </Button>
            {isLastStep ? (
              <Button
                icon={<LucideCheck />}
                color="primary"
                onPress={handleSubmit}
                isDisabled={isSubmitting}
                isPending={isSubmitting}
              >
                {isSubmitting
                  ? t.common.loading.creating
                  : t.brokerage.loads.create.submit}
              </Button>
            ) : (
              <Button
                color="primary"
                variant="solid"
                onPress={handleNext}
                isDisabled={isSubmitting}
              >
                {t.brokerage.loads.create.wizard.buttons.next}
              </Button>
            )}
          </div>
        </div>
      }
    />
  );
};
